import { artistSupabase } from "@/infrastructure/supabase/client";
import type { ProductScopeBinding } from "@/domain/auth/ProductSession";

export type Scope = Pick<ProductScopeBinding, "ownerUserId" | "scopeEpoch">;
export type Revision = number;
export type ProfileRevision = number | string;
export type SaveResult<T> =
	| { readonly kind: "saved"; readonly value: T }
	| { readonly kind: "conflict"; readonly remoteSummary: string }
	| { readonly kind: "capabilityUnavailable" };

export interface ProfileDraft {
	readonly displayName: string;
	readonly bio: string;
	readonly avatar: "none" | "pending";
	readonly background: "none" | "pending";
	readonly avatarURL?: string | null;
	readonly backgroundURL?: string | null;
}

const PROFILE_SELECT = [
	"id",
	"display_name",
	"avatar_url",
	"avatar_pending_url",
	"background_url",
	"background_pending_url",
	"bio",
	"updated_at",
].join(",");

function remoteProfilePayload(
	row: Readonly<Record<string, unknown>>,
): RemoteProfilePayload | null {
	if (
		typeof row.id !== "string" ||
		typeof row.display_name !== "string" ||
		typeof row.updated_at !== "string" ||
		!Number.isFinite(Date.parse(row.updated_at))
	) {
		return null;
	}
	const nullableString = (value: unknown): string | null =>
		typeof value === "string" && value.trim().length > 0 ? value : null;
	const avatarPending = nullableString(row.avatar_pending_url);
	const backgroundPending = nullableString(row.background_pending_url);
	return {
		ownerUserId: row.id.toLowerCase(),
		profile: {
			displayName: row.display_name,
			bio: typeof row.bio === "string" ? row.bio : "",
			avatar: avatarPending === null ? "none" : "pending",
			background: backgroundPending === null ? "none" : "pending",
			avatarURL: avatarPending ?? nullableString(row.avatar_url),
			backgroundURL:
				backgroundPending ?? nullableString(row.background_url),
		},
		revision: row.updated_at,
	};
}

function appendCommentReply(
	comments: readonly Comment[],
	parentId: string,
	reply: Comment,
): { readonly comments: Comment[]; readonly found: boolean } {
	let found = false;
	const next = comments.map((comment) => {
		if (comment.id === parentId) {
			found = true;
			return { ...comment, replies: [...comment.replies, reply] };
		}
		const nested = appendCommentReply(comment.replies, parentId, reply);
		if (nested.found) {
			found = true;
			return { ...comment, replies: nested.comments };
		}
		return comment;
	});
	return { comments: next, found };
}
export interface TrackDraft {
	readonly title: string;
	readonly continents: readonly string[];
	readonly cover: "none" | "pending";
	readonly lyricsMode: "instrumental" | "lyrics";
}
export interface Conversation {
	readonly id: string;
	readonly participants: readonly string[];
	readonly snippet: string;
	readonly at: string;
	readonly unread: number;
	readonly muted?: boolean;
	readonly blocked?: boolean;
}
export interface Message {
	readonly id: string;
	readonly clientId: string;
	readonly body: string;
	readonly author: "me" | "other";
	readonly at: number;
	readonly state: "sending" | "sent" | "failed";
}
export interface Comment {
	readonly id: string;
	readonly author: string;
	readonly body: string;
	readonly at: string;
	readonly likes: number;
	readonly liked: boolean;
	readonly replies: readonly Comment[];
}
export interface CommentPage {
	readonly comments: readonly Comment[];
	readonly hasMore: boolean;
}

export interface SocialRepository {
	loadProfile(
		scope: Scope,
	): Promise<{ readonly value: ProfileDraft; readonly revision: ProfileRevision }>;
	saveProfile(
		scope: Scope,
		draft: ProfileDraft,
		revision: ProfileRevision,
	): Promise<SaveResult<{ readonly revision: ProfileRevision }>>;
	loadTrack(
		scope: Scope,
		id: string,
	): Promise<{ readonly value: TrackDraft; readonly revision: Revision }>;
	saveTrack(
		scope: Scope,
		id: string,
		draft: TrackDraft,
		revision: Revision,
	): Promise<SaveResult<{ readonly revision: Revision }>>;
	loadInbox(scope: Scope): Promise<readonly Conversation[]>;
	createConversation(scope: Scope, participant: string): Promise<Conversation>;
	deleteConversation(scope: Scope, id: string): Promise<void>;
	setConversationMuted(scope: Scope, id: string, muted: boolean): Promise<void>;
	setConversationBlocked(scope: Scope, id: string, blocked: boolean): Promise<void>;
	markRead(scope: Scope, id: string): Promise<void>;
	loadMessages(scope: Scope, id: string): Promise<readonly Message[]>;
	sendMessage(
		scope: Scope,
		id: string,
		message: Message,
	): Promise<{ readonly kind: "sent" } | { readonly kind: "failed" }>;
	removeMessage(scope: Scope, id: string, clientId: string): Promise<void>;
	clearMessages(scope: Scope, id: string): Promise<void>;
	loadComments(scope: Scope, id: string, offset: number): Promise<CommentPage>;
	postComment(
		scope: Scope,
		threadId: string,
		body: string,
		parentId?: string,
	): Promise<{ readonly kind: "saved"; readonly comment: Comment } | { readonly kind: "failed" }>;
	likeComment(
		scope: Scope,
		id: string,
		liked: boolean,
	): Promise<{ readonly kind: "saved" } | { readonly kind: "failed" }>;
}

interface LocalSocialState {
	profile: ProfileDraft;
	profileRevision: number;
	tracks: Record<string, TrackDraft>;
	trackRevisions: Record<string, number>;
	inbox: Conversation[];
	messages: Record<string, Message[]>;
	comments: Record<string, Comment[]>;
}

const memoryStates = new Map<string, LocalSocialState>();
const LOCAL_SOCIAL_SCHEMA = "artist.social.v1";

function emptyState(): LocalSocialState {
	return {
		profile: {
			displayName: "Artist",
			bio: "",
			avatar: "none",
			background: "none",
			avatarURL: null,
			backgroundURL: null,
		},
		profileRevision: 1,
		tracks: {},
		trackRevisions: {},
		inbox: [],
		messages: {},
		comments: {},
	};
}

function storageKey(scope: Scope): string {
	return `${LOCAL_SOCIAL_SCHEMA}:${scopeKey(scope)}`;
}

function cloneState(state: LocalSocialState): LocalSocialState {
	return JSON.parse(JSON.stringify(state)) as LocalSocialState;
}

function readState(scope: Scope): LocalSocialState {
	const key = storageKey(scope);
	if (typeof window !== "undefined") {
		const raw = window.localStorage.getItem(key);
		if (raw !== null) {
			try {
				const parsed: unknown = JSON.parse(raw);
				if (isLocalSocialState(parsed)) return parsed;
			} catch {
				window.localStorage.removeItem(key);
			}
		}
	}
	const existing = memoryStates.get(key);
	if (existing !== undefined) return cloneState(existing);
	const created = emptyState();
	memoryStates.set(key, cloneState(created));
	return created;
}

function writeState(scope: Scope, state: LocalSocialState): void {
	const key = storageKey(scope);
	const snapshot = cloneState(state);
	memoryStates.set(key, snapshot);
	if (typeof window !== "undefined") {
		window.localStorage.setItem(key, JSON.stringify(snapshot));
	}
}

function isLocalSocialState(value: unknown): value is LocalSocialState {
	if (!isRecord(value)) return false;
	return (
		isRecord(value.profile) &&
		Number.isSafeInteger(value.profileRevision) &&
		isRecord(value.tracks) &&
		isRecord(value.trackRevisions) &&
		Array.isArray(value.inbox) &&
		isRecord(value.messages) &&
		isRecord(value.comments)
	);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function blankTrack(): TrackDraft {
	return {
		title: "",
		continents: [],
		cover: "none",
		lyricsMode: "instrumental",
	};
}

function newId(prefix: string): string {
	const suffix =
		typeof crypto !== "undefined" && "randomUUID" in crypto
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	return `${prefix}-${suffix}`;
}

function updateCommentLike(
	comments: readonly Comment[],
	id: string,
	liked: boolean,
): { readonly comments: Comment[]; readonly found: boolean } {
	let found = false;
	const next = comments.map((comment) => {
		if (comment.id === id) {
			found = true;
			return {
				...comment,
				liked,
				likes: Math.max(0, comment.likes + (liked === comment.liked ? 0 : liked ? 1 : -1)),
			};
		}
		const nested = updateCommentLike(comment.replies, id, liked);
		if (nested.found) {
			found = true;
			return { ...comment, replies: nested.comments };
		}
		return comment;
	});
	return { comments: next, found };
}

/** User-scoped browser persistence for product flows that do not require a server mutation. */
export const offlineSocialRepository: SocialRepository = {
	async loadProfile(scope) {
		const state = readState(scope);
		return {
			value: {
				...state.profile,
				avatarURL: state.profile.avatarURL ?? null,
				backgroundURL: state.profile.backgroundURL ?? null,
			},
			revision: state.profileRevision,
		};
	},
	async saveProfile(scope, draft, revision) {
		const state = readState(scope);
		if (revision !== state.profileRevision) {
			return { kind: "conflict", remoteSummary: "资料已在另一个页面更新。" };
		}
		state.profile = { ...draft };
		state.profileRevision += 1;
		writeState(scope, state);
		return { kind: "saved", value: { revision: state.profileRevision } };
	},
	async loadTrack(scope, id) {
		const state = readState(scope);
		return {
			value: { ...(state.tracks[id] ?? blankTrack()) },
			revision: state.trackRevisions[id] ?? 1,
		};
	},
	async saveTrack(scope, id, draft, revision) {
		const state = readState(scope);
		const currentRevision = state.trackRevisions[id] ?? 1;
		if (revision !== currentRevision) {
			return { kind: "conflict", remoteSummary: "歌曲已在另一个页面更新。" };
		}
		state.tracks[id] = { ...draft, continents: [...draft.continents] };
		state.trackRevisions[id] = currentRevision + 1;
		writeState(scope, state);
		return { kind: "saved", value: { revision: currentRevision + 1 } };
	},
	async loadInbox(scope) {
		return readState(scope).inbox.map((conversation) => ({
			...conversation,
			participants: [...conversation.participants],
		}));
	},
	async createConversation(scope, participant) {
		const normalized = participant.trim();
		if (normalized.length === 0) throw new TypeError("participant is required");
		const state = readState(scope);
		const existing = state.inbox.find((conversation) =>
			conversation.participants.some(
				(value) => value !== "你" && value.toLowerCase() === normalized.toLowerCase(),
			),
		);
		if (existing !== undefined) return { ...existing };
		const conversation: Conversation = {
			id: newId("conversation"),
			participants: [normalized, "你"],
			snippet: "开始新的对话",
			at: "刚刚",
			unread: 0,
			muted: false,
			blocked: false,
		};
		state.inbox = [conversation, ...state.inbox];
		state.messages[conversation.id] = [];
		writeState(scope, state);
		return { ...conversation };
	},
	async deleteConversation(scope, id) {
		const state = readState(scope);
		state.inbox = state.inbox.filter((conversation) => conversation.id !== id);
		delete state.messages[id];
		writeState(scope, state);
	},
	async setConversationMuted(scope, id, muted) {
		const state = readState(scope);
		state.inbox = state.inbox.map((conversation) =>
			conversation.id === id ? { ...conversation, muted } : conversation,
		);
		writeState(scope, state);
	},
	async setConversationBlocked(scope, id, blocked) {
		const state = readState(scope);
		state.inbox = state.inbox.map((conversation) =>
			conversation.id === id ? { ...conversation, blocked } : conversation,
		);
		writeState(scope, state);
	},
	async markRead(scope, id) {
		const state = readState(scope);
		state.inbox = state.inbox.map((conversation) =>
			conversation.id === id ? { ...conversation, unread: 0 } : conversation,
		);
		writeState(scope, state);
	},
	async loadMessages(scope, id) {
		return (readState(scope).messages[id] ?? []).map((message) => ({ ...message }));
	},
	async sendMessage(scope, id, message) {
		const state = readState(scope);
		const conversationIndex = state.inbox.findIndex((item) => item.id === id);
		if (conversationIndex < 0) return { kind: "failed" };
		const conversation = state.inbox[conversationIndex];
		if (conversation === undefined) return { kind: "failed" };
		state.messages[id] = [...(state.messages[id] ?? []), { ...message, state: "sent" }];
		state.inbox[conversationIndex] = {
			...conversation,
			snippet: message.body,
			at: "刚刚",
			unread: 0,
		};
		writeState(scope, state);
		return { kind: "sent" };
	},
	async removeMessage(scope, id, clientId) {
		const state = readState(scope);
		state.messages[id] = (state.messages[id] ?? []).filter(
			(message) => message.clientId !== clientId,
		);
		writeState(scope, state);
	},
	async clearMessages(scope, id) {
		const state = readState(scope);
		state.messages[id] = [];
		const index = state.inbox.findIndex((conversation) => conversation.id === id);
		if (index >= 0) {
			const conversation = state.inbox[index];
			if (conversation === undefined) return;
			state.inbox[index] = {
				...conversation,
				snippet: "开始新的对话",
				at: "刚刚",
				unread: 0,
			};
		}
		writeState(scope, state);
	},
	async loadComments(scope, id, offset) {
		const comments = readState(scope).comments[id] ?? [];
		const pageSize = 20;
		return {
			comments: comments.slice(offset, offset + pageSize),
			hasMore: offset + pageSize < comments.length,
		};
	},
	async postComment(scope, threadId, body, parentId) {
		const normalized = body.trim();
		if (normalized.length === 0) return { kind: "failed" };
		const state = readState(scope);
		const comment: Comment = {
			id: newId("comment"),
			author: state.profile.displayName.trim() || "Artist",
			body: normalized,
			at: "刚刚",
			likes: 0,
			liked: false,
			replies: [],
		};
		const current = state.comments[threadId] ?? [];
		if (parentId === undefined) {
			state.comments[threadId] = [...current, comment];
		} else {
			const appended = appendCommentReply(current, parentId, comment);
			if (!appended.found) return { kind: "failed" };
			state.comments[threadId] = appended.comments;
		}
		writeState(scope, state);
		return { kind: "saved", comment };
	},
	async likeComment(scope, id, liked) {
		const state = readState(scope);
		for (const threadId of Object.keys(state.comments)) {
			const updated = updateCommentLike(state.comments[threadId] ?? [], id, liked);
			if (updated.found) {
				state.comments[threadId] = updated.comments;
				writeState(scope, state);
				return { kind: "saved" };
			}
		}
		return { kind: "failed" };
	},
};

interface RemoteProfilePayload {
	readonly ownerUserId: string;
	readonly profile: ProfileDraft;
	readonly revision: string;
}

function isRemoteProfilePayload(value: unknown, scope: Scope): value is RemoteProfilePayload {
	if (!isRecord(value) || value.ownerUserId !== scope.ownerUserId.toLowerCase()) return false;
	if (typeof value.revision !== "string" || !Number.isFinite(Date.parse(value.revision))) {
		return false;
	}
	const profile = value.profile;
	if (!isRecord(profile)) return false;
	return (
		typeof profile.displayName === "string" &&
		typeof profile.bio === "string" &&
		(profile.avatar === "none" || profile.avatar === "pending") &&
		(profile.background === "none" || profile.background === "pending") &&
		(profile.avatarURL === null || typeof profile.avatarURL === "string") &&
		(profile.backgroundURL === null || typeof profile.backgroundURL === "string")
	);
}

export const browserSocialRepository: SocialRepository = {
	...offlineSocialRepository,
	async loadProfile(scope) {
		const { data, error } = await artistSupabase()
			.from("profiles")
			.select(PROFILE_SELECT)
			.eq("id", scope.ownerUserId.toLowerCase())
			.single();
		if (error || data === null) throw new Error("profile-load-failed");
		const payload = remoteProfilePayload(data as unknown as Record<string, unknown>);
		if (payload === null || !isRemoteProfilePayload(payload, scope)) {
			throw new Error("profile-response-invalid");
		}
		return { value: payload.profile, revision: payload.revision };
	},
	async saveProfile(scope, draft, revision) {
		if (typeof revision !== "string") return { kind: "capabilityUnavailable" };
		try {
			const displayName = draft.displayName.trim();
			if (displayName.length === 0 || displayName.length > 200 || draft.bio.length > 2_000) {
				return { kind: "capabilityUnavailable" };
			}
			const { data, error } = await artistSupabase()
				.from("profiles")
				.update({ display_name: displayName, bio: draft.bio })
				.eq("id", scope.ownerUserId.toLowerCase())
				.eq("updated_at", revision)
				.select(PROFILE_SELECT)
				.maybeSingle();
			if (error) return { kind: "capabilityUnavailable" };
			if (data === null) {
				return {
					kind: "conflict",
					remoteSummary: "资料已在其他设备更新。",
				};
			}
			const payload = remoteProfilePayload(data as unknown as Record<string, unknown>);
			if (payload === null || !isRemoteProfilePayload(payload, scope)) {
				return { kind: "capabilityUnavailable" };
			}
			return { kind: "saved", value: { revision: payload.revision } };
		} catch {
			return { kind: "capabilityUnavailable" };
		}
	},
};

export function sameScope(
	a: Scope | null | undefined,
	b: Scope | null | undefined,
): boolean {
	return (
		a !== null &&
		a !== undefined &&
		b !== null &&
		b !== undefined &&
		a.ownerUserId === b.ownerUserId &&
		a.scopeEpoch === b.scopeEpoch
	);
}

export function scopeKey(scope: Scope): string {
	return `${scope.ownerUserId}:${scope.scopeEpoch}`;
}
